import { useState, useEffect, useRef } from 'react';
import { X, Tag, Plus, Check, Loader2, ChevronDown } from 'lucide-react';
import { Modal } from '@mui/material';
import baseapi from '../../services/baseapi';
import { showError, showSuccess } from '../../utils/toast';

interface TagData {
    id: number;
    name: string;
    organization_id: number;
    client_id: number | null;
}

interface AddTagModalProps {
    open: boolean;
    onClose: () => void;
    assetId: number | null;
    assetName: string;
    onSuccess: () => void;
}

const AddTagModal: React.FC<AddTagModalProps> = ({ open, onClose, assetId, assetName, onSuccess }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [availableTags, setAvailableTags] = useState<TagData[]>([]);
    const [selectedTags, setSelectedTags] = useState<TagData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (open) {
            fetchTags(); setSelectedTags([]); setSearchTerm(''); setIsDropdownOpen(false);
        }
    }, [open, assetId]);

    const fetchTags = async (query: string = '') => {
        setIsLoading(true);
        try {
            const queryObj: any = {};
            if (query.trim()) queryObj.name = { "$ilike": `%${query}%` };
            const params = { query: JSON.stringify(queryObj), limit: 20 };
            const response = await baseapi.get('/tags', { params });
            if (response.data.status) setAvailableTags(response.data.data);
        } catch (error) { console.error('Fetch tags failed', error); }
        finally { setIsLoading(false); }
    };

    useEffect(() => {
        const timer = setTimeout(() => { if (open) fetchTags(searchTerm); }, 200);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const handleCreateTag = async () => {
        if (!searchTerm.trim()) return;
        setIsCreating(true);
        try {
            const response = await baseapi.post('/tags', { name: searchTerm.trim() });
            if (response.data.status) {
                const newTag: TagData = { id: response.data.data.id, name: searchTerm.trim(), organization_id: 0, client_id: null };
                showSuccess('Tag created'); setSelectedTags(prev => [...prev, newTag]); setSearchTerm(''); fetchTags();
            }
        } catch (error: any) { showError(error.message); }
        finally { setIsCreating(false); }
    };

    const handleToggleTag = (tag: TagData) => {
        setSelectedTags(prev => {
            const isSelected = prev.find(t => t.id === tag.id);
            if (isSelected) return prev.filter(t => t.id !== tag.id);
            return [...prev, tag];
        });
    };

    const handleSave = async () => {
        if (!assetId) return;
        setIsSaving(true);
        try {
            const tagIds = selectedTags.map(t => t.id);
            const response = await baseapi.put(`/assets/${assetId}`, { tag_ids: tagIds });
            if (response.data.status) { showSuccess('Tags updated'); onSuccess(); onClose(); }
        } catch (error: any) { showError(error.message); }
        finally { setIsSaving(false); }
    };

    return (
        <Modal open={open} onClose={onClose} className="flex items-center justify-center p-4">
            <div className="w-full max-w-sm bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 outline-none">
                <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                    <div className="flex items-center gap-2.5">
                        <div className="p-1.5 bg-[#422568] rounded-lg"><Tag className="w-3.5 h-3.5 text-white" /></div>
                        <div>
                            <span className="text-[11px] font-black uppercase tracking-widest text-slate-900">Label Management</span>
                            <p className="text-[9px] text-slate-500 font-bold uppercase tracking-tighter leading-none mt-0.5">{assetName}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors"><X className="w-4 h-4" /></button>
                </div>

                <div className="p-5 space-y-4">
                    <div className="relative" ref={dropdownRef}>
                        <div onClick={() => setIsDropdownOpen(!isDropdownOpen)} className={`min-h-[40px] w-full bg-slate-50 border ${isDropdownOpen ? 'border-[#422568]' : 'border-slate-200'} rounded-xl p-2 flex flex-wrap gap-1.5 cursor-text transition-all items-center pr-8`}>
                            {selectedTags.length === 0 && !searchTerm && <span className="text-[11px] font-bold text-slate-400 ml-1">Assign labels...</span>}
                            {selectedTags.map(tag => (
                                <span key={tag.id} className="flex items-center gap-1.5 px-2 py-0.5 bg-[#422568]/10 border border-[#422568] text-[#422568] text-[9px] font-black rounded-lg uppercase tracking-wider">
                                    {tag.name}
                                    <X className="w-2.5 h-2.5 cursor-pointer hover:text-[#422568]" onClick={(e) => { e.stopPropagation(); handleToggleTag(tag); }} />
                                </span>
                            ))}
                            <input type="text" className="bg-transparent border-none outline-none text-[11px] font-bold text-slate-900 flex-1 min-w-[60px] py-1" value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); if (!isDropdownOpen) setIsDropdownOpen(true); }} onClick={(e) => e.stopPropagation()} />
                            <ChevronDown className={`absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                        </div>

                        {isDropdownOpen && (
                            <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.1)] z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                                <div className="max-h-[200px] overflow-y-auto custom-scroll p-1.5">
                                    {availableTags.length === 0 && !searchTerm && !isLoading && <p className="py-4 text-[10px] text-slate-400 text-center font-bold uppercase tracking-widest italic">No matches found</p>}
                                    {availableTags.map(tag => {
                                        const isSelected = selectedTags.some(t => t.id === tag.id);
                                        return (
                                            <button key={tag.id} onClick={(e) => { e.stopPropagation(); handleToggleTag(tag); }} className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-[11px] font-bold transition-all ${isSelected ? 'bg-[#422568] text-[#422568]' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}>
                                                <div className="flex items-center gap-2.5">
                                                    <Tag className={`w-3 h-3 ${isSelected ? 'text-[#422568]' : 'text-slate-300'}`} />
                                                    <span>{tag.name}</span>
                                                </div>
                                                {isSelected && <Check className="w-3 h-3 text-[#422568]" strokeWidth={4} />}
                                            </button>
                                        );
                                    })}
                                    {searchTerm && !availableTags.some(t => t.name.toLowerCase() === searchTerm.toLowerCase()) && (
                                        <button onClick={(e) => { e.stopPropagation(); handleCreateTag(); }} disabled={isCreating} className="w-full flex items-center gap-2.5 px-3 py-2.5 text-[#422568] bg-[#422568] hover:bg-[#422568] rounded-lg mt-1 border border-dashed border-[#422568] transition-colors">
                                            {isCreating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                                            <span className="text-[10px] font-black uppercase tracking-widest">Construct "{searchTerm}"</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{selectedTags.length} LABELS</span>
                    <div className="flex gap-2">
                        <button onClick={onClose} className="px-3 py-1.5 text-[10px] font-black text-slate-500 hover:text-slate-900 transition-all uppercase tracking-widest">Dismiss</button>
                        <button onClick={handleSave} disabled={isSaving || selectedTags.length === 0} className="px-5 py-1.5 text-[10px] font-black text-white bg-[#422568] hover:bg-[#422568] rounded-xl shadow-lg shadow-[#422568]/20 disabled:opacity-30 transition-all flex items-center gap-2 uppercase tracking-widest">
                            {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Update
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default AddTagModal;
